import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Fab,
  Zoom,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Theme und Komponenten
import TritonTheme, { tritonStyles } from '../styles/TritonTheme';
import TritonHeader from './TritonHeader';
import TritonSidebar from './TritonSidebar';
import TritonDashboard from './TritonDashboard';

// Legacy Components
// 🚀 CODE-SPLITTING: Lazy Loading für schwere Komponenten
const AddBuchung = lazy(() => import('./AddBuchung'));
const KontenplanVerwaltung = lazy(() => import('./KontenplanVerwaltung'));
const BuchungsManagement = lazy(() => import('./BuchungsManagement'));
const MietvertragManagement = lazy(() => import('./MietvertragManagementNew'));
const MietanpassungenUebersicht = lazy(() => import('./MietanpassungenUebersicht'));
const VerbessertesForderungsmanagement = lazy(() => import('./forderungsmanagement'));
const ForderungsmanagementMain = lazy(() => import('./forderungsmanagement/components/ForderungsmanagementMain'));
const EnergieDokumentInboxPage = lazy(() => import('./forderungsmanagement/components/EnergieDokumentInboxPage'));
const BelegUploadProfessional = lazy(() => import('./BelegUploadProfessional'));
const DokumentenEingangPage = lazy(() => import('./dokumente/DokumentenEingangPage'));
const Wohneinheiten = lazy(() => import('./WohneinheitenNew'));
const ObjectManagement = lazy(() => import('./ObjectManagement'));
const NewObjectPage = lazy(() => import('./NewObjectPage'));
const VerkaufsangeboteManagement = lazy(() => import('./VerkaufsangeboteManagement'));
const UserManagement = lazy(() => import('./UserManagement'));
const CsvUpload = lazy(() => import('./CsvUpload'));
const KontobewegungManagement = lazy(() => import('./KontobewegungManagement'));
const CashFlowDashboard = lazy(() => import('./cashflow'));
const TodoManagement = lazy(() => import('./TodoManagement'));
const Vertraege = lazy(() => import('./bankwesen/Vertraege'));
const USTVoranmeldung = lazy(() => import('./steuern/USTVoranmeldung'));
const NebenkostenUstPrognose = lazy(() => import('./steuern/NebenkostenUstPrognose'));
const AnlageVV = lazy(() => import('./steuern/AnlageVV'));
const AfaVerwaltung = lazy(() => import('./steuern/AfaVerwaltung'));
const Nebenkostenabrechnung = lazy(() => import('../pages/Nebenkostenabrechnung'));
const NKZaehlerstaende = lazy(() => import('../pages/NKZaehlerstaende'));
const NKZaehlerManagement = lazy(() => import('../pages/NKZaehlerManagement'));
const BankwesenPage = lazy(() => import('../pages/Bankwesen'));
const DreiStapelVerarbeitung = lazy(() => import('./DreiStapelVerarbeitung'));

const LazyBoundary = ({ children }) => (
  <Suspense
    fallback={
      <Backdrop open sx={{ zIndex: 9999, bgcolor: 'rgba(0,0,0,0.7)' }}>
        <CircularProgress color="primary" size={60} />
      </Backdrop>
    }
  >
    {children}
  </Suspense>
);

// Einfache ErrorBoundary, um Lazy-Load-Fehler sichtbar zu machen
// RouteErrorBoundary entfällt hier, da direkte Einbindung

// Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#f7fafc',
}));

const ContentArea = styled(Box)(({ theme, ismobile }) => ({
  flex: 1,
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'hidden',
  marginLeft: ismobile === 'true' ? 0 : '280px',
  marginTop: '80px',
  padding: theme.spacing(0),
  minHeight: 'calc(100vh - 80px)',
  backgroundColor: '#f7fafc',
}));

const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#ffffff',
  background: 'rgba(26, 54, 93, 0.8)',
  backdropFilter: 'blur(4px)',
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  background: tritonStyles.gradients.primary,
  color: '#ffffff',
  zIndex: 1000,
  '&:hover': {
    background: tritonStyles.gradients.primaryHover,
    transform: 'scale(1.05)',
  },
}));

// Legacy Wrapper für einheitliches Styling
const LegacyWrapper = ({ children }) => (
  <Box sx={{ 
    p: 3, 
    backgroundColor: '#ffffff',
    borderRadius: 2,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    margin: 2
  }}>
    {children}
  </Box>
);

// Cash-Flow Komponente wurde in modulare Struktur ausgelagert
// Siehe: /components/cashflow/

// Fallback-Overlay für unmatched Routen innerhalb TritonLayout
function TritonRouteFallback() {
  const loc = useLocation();
  useEffect(() => {
    console.log('🧭 [Fallback] Unmatched path in TritonLayout:', loc.pathname);
  }, [loc.pathname]);
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: '#00eaff',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 36,
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div>
        ⛔ TritonLayout Fallback<br/>
        Kein Route-Match für: <strong>{loc.pathname}</strong>
      </div>
    </div>
  );
}

const TritonLayout = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [fabVisible, setFabVisible] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();
  const theme = TritonTheme;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Sidebar nur auf Mobile togglebar, auf Desktop immer offen
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Initialisierung
  useEffect(() => {
    // Simuliere App-Initialisierung
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Scroll-Detection für FAB
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setFabVisible(currentScrollY < lastScrollY || currentScrollY < 100);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    }
  };

  const handleLogout = async () => {
    try {
      // API-Call zum Backend
      await axios.post('/api/auth/logout');
      
      // Session löschen
      sessionStorage.clear();
      localStorage.removeItem('user');
      
      // Falls onLogout Prop vorhanden, rufe sie auf (für Parent-Komponente)
      if (onLogout) {
        onLogout();
      }
      
      // Harte Redirect zur Landingpage
      window.location.href = '/';
    } catch (error) {
      console.error('Logout-Fehler:', error);
      // Im Fehlerfall trotzdem zur Landingpage
      sessionStorage.clear();
      localStorage.removeItem('user');
      window.location.href = '/';
    }
  };

  const handleFabClick = () => {
    // Navigiere zu Beleg-Upload als Hauptaktion
    navigate('/dashboard/belege');
  };

  // Nutze echten User aus Props (kommt von AuthContext)
  const currentUser = user || {
    name: 'Benutzer',
    email: '',
    role: 'Benutzer',
  };

  // Routing-Diagnose (muss vor Returns/Hooks-Ordnung platziert sein)
  useEffect(() => {
    console.log('🧭 [Router] location changed:', location.pathname);
  }, [location.pathname]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <LoadingBackdrop open={loading}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={60} 
              sx={{ 
                color: '#d69e2e',
                mb: 2,
              }} 
            />
            <Box sx={{ mt: 2 }}>
              <div style={{ 
                fontSize: '1.2rem', 
                fontWeight: 500, 
                marginBottom: '0.5rem',
                color: '#ffffff'
              }}>
                Immobilienverwaltung wird geladen
              </div>
              <div style={{ 
                fontSize: '0.9rem', 
                opacity: 0.8,
                color: '#ffffff'
              }}>
                Triton Design System wird initialisiert...
              </div>
            </Box>
          </Box>
        </LoadingBackdrop>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MainContainer>
        {/* Header */}
        <TritonHeader
          user={currentUser}
          onLogout={handleLogout}
          onMenuToggle={handleSidebarToggle}
        />

        {/* Sidebar */}
        <TritonSidebar
          open={isMobile ? mobileSidebarOpen : true}
          onClose={() => setMobileSidebarOpen(false)}
          currentPath={location.pathname}
          variant={isMobile ? 'temporary' : 'permanent'}
        />

        {/* Hauptinhalt mit Routing */}
        <ContentArea ismobile={isMobile.toString()}>
          <Routes>
            {/* BANKWESEN - zurück zur echten Page (Overlay-Debug kommt aus Page selbst) */}
            <Route path="/dashboard/bankwesen" element={<LazyBoundary><BankwesenPage /></LazyBoundary>} />
            <Route path="bankwesen" element={<LazyBoundary><BankwesenPage /></LazyBoundary>} />
            
            {/* TEST: Verschiedene Pfad-Varianten */}
            <Route path="dashboard/nebenkostenabrechnung" element={
              <div style={{ 
                padding: '40px', 
                backgroundColor: '#2ecc71', 
                color: 'white', 
                fontSize: '24px',
                textAlign: 'center',
                margin: '20px'
              }}>
                <h1>🟢 RELATIVER PFAD FUNKTIONIERT!</h1>
                <p>Route: dashboard/nebenkostenabrechnung (ohne führenden /)</p>
              </div>
            } />
            
            <Route path="/dashboard/nebenkostenabrechnung" element={
              <div style={{ 
                padding: '40px', 
                backgroundColor: '#3498db', 
                color: 'white', 
                fontSize: '24px',
                textAlign: 'center',
                margin: '20px'
              }}>
                <h1>� ABSOLUTER PFAD FUNKTIONIERT!</h1>
                <p>Route: /dashboard/nebenkostenabrechnung (mit führendem /)</p>
              </div>
            } />
            
            <Route
              path="nebenkostenabrechnung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><Nebenkostenabrechnung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* Nebenkostenabrechnung Routes - KORRIGIERT mit kurzen Pfaden */}
            <Route
              path="nebenkostenabrechnung/zaehlerstaende"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NKZaehlerstaende /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="nebenkostenabrechnung/zaehler-erfassen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NKZaehlerManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* Wildcard route entfernt - war das Problem! */}
            
            {/* TEST ROUTE - sollte nur bei /dashboard/nebenkostenabrechnung sichtbar sein */}
            <Route path="/dashboard/nebenkostenabrechnung" element={
              <div style={{ 
                padding: '40px', 
                backgroundColor: '#2ecc71', 
                color: 'white', 
                fontSize: '24px',
                textAlign: 'center',
                margin: '20px'
              }}>
                <h1>🟢 ROUTE MATCHED ERFOLGREICH!</h1>
                <p>Sie sind auf /dashboard/nebenkostenabrechnung</p>
                <p>Diese Box sollte NUR hier sichtbar sein!</p>
              </div>
            } />
            
            {/* Dashboard als Standard */}
            <Route path="/" element={<TritonDashboard />} />
            <Route path="/dashboard" element={<TritonDashboard />} />
            
            {/* Buchungen */}
            <Route
              path="/dashboard/buchung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AddBuchung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/buchungen"
              element={<LazyBoundary><BuchungsManagement /></LazyBoundary>}
            />
            <Route
              path="/dashboard/kontenplan"
              element={<LazyBoundary><KontenplanVerwaltung /></LazyBoundary>}
            />
            
            {/* Transaktionen */}
            <Route
              path="/dashboard/kontobewegungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><KontobewegungManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* Cash-Flow - Modulare professionelle Version */}
            <Route path="cashflow" element={<LazyBoundary><CashFlowDashboard /></LazyBoundary>} />
            
            {/* ToDo Management - Kanban Board mit Drag & Drop */}
            <Route path="/dashboard/todo-management" element={<LazyBoundary><TodoManagement /></LazyBoundary>} />
            <Route path="todo-management" element={<LazyBoundary><TodoManagement /></LazyBoundary>} />
            
            {/* Immobilien */}
            <Route
              path="/dashboard/objekte"
              element={
                <LazyBoundary>
                  <LegacyWrapper><ObjectManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/objekte/neu"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NewObjectPage /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/verkaufsangebote"
              element={
                <LazyBoundary>
                  <LegacyWrapper><VerkaufsangeboteManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/wohneinheiten"
              element={
                <LazyBoundary>
                  <LegacyWrapper><Wohneinheiten /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/mietvertrag"
              element={
                <LazyBoundary>
                  <LegacyWrapper><MietvertragManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/mietanpassungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><MietanpassungenUebersicht /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="mietanpassungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><MietanpassungenUebersicht /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/mietforderungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><VerbessertesForderungsmanagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/forderungsmanagement"
              element={<LazyBoundary><ForderungsmanagementMain /></LazyBoundary>}
            />
            <Route
              path="forderungsmanagement"
              element={<LazyBoundary><ForderungsmanagementMain /></LazyBoundary>}
            />
            
            {/* Darlehensverträge - Bankwesen - ROUTE GELÖSCHT, ist weiter oben */}
            <Route
              path="/dashboard/darlehensvertraege"
              element={
                <LazyBoundary>
                  <LegacyWrapper><Vertraege /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="darlehensvertraege"
              element={
                <LazyBoundary>
                  <LegacyWrapper><Vertraege /></LegacyWrapper>
                </LazyBoundary>
              }
            />

            {/* Steuern */}
            {/* USt-Voranmeldung */}
            <Route
              path="/dashboard/steuern/ust-voranmeldung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><USTVoranmeldung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/ust-voranmeldung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><USTVoranmeldung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="ust-voranmeldung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><USTVoranmeldung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="steuern/ust-voranmeldung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><USTVoranmeldung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/steuern/nk-ust-prognose"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NebenkostenUstPrognose /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/nk-ust-prognose"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NebenkostenUstPrognose /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="nk-ust-prognose"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NebenkostenUstPrognose /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="steuern/nk-ust-prognose"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NebenkostenUstPrognose /></LegacyWrapper>
                </LazyBoundary>
              }
            />

            {/* Anlage V/V (absolute und relative Pfade, damit beides sicher matched) */}
            <Route
              path="/dashboard/steuern/anlage-vv"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AnlageVV /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dashboard/anlage-vv"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AnlageVV /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="anlage-vv"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AnlageVV /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* AfA-Verwaltung (Abschreibungen) */}
            <Route
              path="/dashboard/steuern/afa-verwaltung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AfaVerwaltung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="steuern/afa-verwaltung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AfaVerwaltung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="afa-verwaltung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AfaVerwaltung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* Dokumente
                Hinweis: TritonLayout ist unter /dashboard/* gemountet – Routen matchen
                gegen den Rest-Pfad (z. B. "belege"), NICHT gegen "/dashboard/belege". */}
            <Route
              path="energie-dokument-inbox"
              element={<LazyBoundary><EnergieDokumentInboxPage /></LazyBoundary>}
            />
            
            {/* Verwaltung */}
            <Route
              path="/dashboard/benutzer"
              element={
                <LazyBoundary>
                  <LegacyWrapper><UserManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            
            {/* Nebenkostenabrechnung Routes */}
            <Route path="/dashboard/nebenkostenabrechnung" element={
              <div style={{ 
                padding: '40px', 
                backgroundColor: '#4CAF50', 
                color: 'white', 
                fontSize: '24px',
                textAlign: 'center',
                margin: '20px'
              }}>
                <h1>🟢 DIREKTE ROUTE FUNKTIONIERT!</h1>
                <p>Diese Route wird direkt in TritonLayout.js gerendert</p>
              </div>
            } />
            <Route path="/dashboard/nebenkostenabrechnung/zaehlerstaende" element={
              <div style={{ 
                padding: '40px', 
                backgroundColor: '#FF9800', 
                color: 'white', 
                fontSize: '24px',
                textAlign: 'center',
                margin: '20px'
              }}>
                <h1>🟠 ZÄHLERSTÄNDE ROUTE FUNKTIONIERT!</h1>
                <p>Diese Route wird direkt in TritonLayout.js gerendert</p>
              </div>
            } />
            
            {/* Legacy Routes für Kompatibilität */}
            <Route
              path="/buchung"
              element={
                <LazyBoundary>
                  <LegacyWrapper><AddBuchung /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/buchungen"
              element={<LazyBoundary><BuchungsManagement /></LazyBoundary>}
            />
            <Route
              path="/kontenplan"
              element={<LazyBoundary><KontenplanVerwaltung /></LazyBoundary>}
            />
            <Route
              path="/kontobewegungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><KontobewegungManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/csv-upload"
              element={
                <LazyBoundary>
                  <LegacyWrapper><CsvUpload /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/objekte"
              element={
                <LazyBoundary>
                  <LegacyWrapper><ObjectManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/objekte/neu"
              element={
                <LazyBoundary>
                  <LegacyWrapper><NewObjectPage /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/verkaufsangebote"
              element={
                <LazyBoundary>
                  <LegacyWrapper><VerkaufsangeboteManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/wohneinheiten"
              element={
                <LazyBoundary>
                  <LegacyWrapper><Wohneinheiten /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/mietvertrag"
              element={
                <LazyBoundary>
                  <LegacyWrapper><MietvertragManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/mietforderungen"
              element={
                <LazyBoundary>
                  <LegacyWrapper><VerbessertesForderungsmanagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/dokumente"
              element={<Navigate to="/dashboard/belege" replace />}
            />
            <Route
              path="/dokumenten-eingang"
              element={
                <LazyBoundary>
                  <LegacyWrapper><DokumentenEingangPage /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/belege"
              element={
                <LazyBoundary>
                  <LegacyWrapper><BelegUploadProfessional /></LegacyWrapper>
                </LazyBoundary>
              }
            />
            <Route
              path="/belegverarbeitung"
              element={<LazyBoundary><DreiStapelVerarbeitung /></LazyBoundary>}
            />
            <Route
              path="/sichere-dokumente"
              element={<Navigate to="/dashboard/belege" replace />}
            />
            <Route
              path="/benutzer"
              element={
                <LazyBoundary>
                  <LegacyWrapper><UserManagement /></LegacyWrapper>
                </LazyBoundary>
              }
            />

            {/* Fallback ganz zum Schluss: zeigt Overlay, wenn NICHTS matched */}
            <Route path="*" element={<TritonRouteFallback />} />
          </Routes>
        </ContentArea>

        {/* Floating Action Button */}
        <Zoom in={fabVisible}>
          <FloatingActionButton
            color="primary"
            aria-label="Beleg hochladen"
            onClick={handleFabClick}
          >
            <AddIcon />
          </FloatingActionButton>
        </Zoom>
      </MainContainer>
    </ThemeProvider>
  );
};

export default TritonLayout;
